import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo } from "react";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: 'auto',
    width: '100%',
    overflow: 'hidden'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  }
});

export const EconomicsNI51101Table1Table = ({ economics }) => {

  // STYLES
  const classes = useStyles();

  const rows = useMemo(() => {
    try {
      const data = [];
      const wellType = economics?.economicVolumesGross?.oil > 0 ? 'oil' : 'gas';


      ['Proved Developed Producing', 'Proved Developed Non-Producing', 'Proved Undeveloped', 'Total Proved'].forEach(category => {
        data.push({
          id: category,
          oilGross: 0,
          oilNet: 0,
          solutionGasGross: 0,
          solutionGasNet: 0,
          assGasGross: 0,
          assGasNet: 0,
          nglGross: 0,
          nglNet: 0,
          boeGross: 0,
          boeNet: 0
        });
      });

      ['Total Probable', 'Total Proved + Probable'].forEach(category => {
        data.push({
          id: category,
          oilGross: getVolume('oil', 'companyGross', wellType),
          oilNet: getVolume('oil', 'companyNet', wellType),
          solutionGasGross: getVolume('solutionGas', 'companyGross', wellType),
          solutionGasNet: getVolume('solutionGas', 'companyNet', wellType),
          assGasGross: getVolume('assGas', 'companyGross', wellType),
          assGasNet: getVolume('assGas', 'companyNet', wellType),
          nglGross: getVolume('ngl', 'companyGross', wellType),
          nglNet: getVolume('ngl', 'companyNet', wellType),
          boeGross: getVolume('boe', 'companyGross', wellType),
          boeNet: getVolume('boe', 'companyNet', wellType)
        });
      });

      return data;

      function getVolume(fluid, type, wellType) {
        switch(fluid) {
          case 'oil':
            if (type === 'gross') {
              return Number((economics?.economicVolumesGross?.oil / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyGross') {
              return Number((economics?.economicVolumesNet?.oil / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyNet') {
              return Number((economics?.economicVolumesCompanyNet?.oil / 1000)?.toFixed(2)) || 0;
            };
            break;
          case 'solutionGas':
            if (wellType === 'oil') {
              if (type === 'gross') {
                return Number((economics?.economicVolumesGross?.salesGas)?.toFixed(2)) || 0;
              } else if (type === 'companyGross') {
                return Number((economics?.economicVolumesNet?.salesGas)?.toFixed(2)) || 0;
              } else if (type === 'companyNet') {
                return Number((economics?.economicVolumesCompanyNet?.salesGas)?.toFixed(2)) || 0;
              };
            } else {
              return 0;
            };
            break;
          case 'assGas':
            if (wellType === 'gas') {
              if (type === 'gross') {
                return Number((economics?.economicVolumesGross?.salesGas)?.toFixed(2)) || 0;
              } else if (type === 'companyGross') {
                return Number((economics?.economicVolumesNet?.salesGas)?.toFixed(2)) || 0;
              } else if (type === 'companyNet') {
                return Number((economics?.economicVolumesCompanyNet?.salesGas)?.toFixed(2)) || 0;
              };
            } else {
              return 0;
            };
            break;
          case 'ngl':
            if (type === 'gross') {
              return Number(((economics?.economicVolumesGross?.ngl + economics?.economicVolumesGross?.condensate) / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyGross') {
              return Number(((economics?.economicVolumesNet?.ngl + economics?.economicVolumesNet?.condensate) / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyNet') {
              return Number(((economics?.economicVolumesCompanyNet?.ngl + economics?.economicVolumesCompanyNet?.condensate) / 1000)?.toFixed(2)) || 0;
            };
            break;
          case 'boe':
            if (type === 'gross') {
              return Number((economics?.cashflowGross?.reduce((prev, curr) => prev + curr.boe, 0) / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyGross') {
              return Number((economics?.cashflowNet?.reduce((prev, curr) => prev + curr.boe, 0) / 1000)?.toFixed(2)) || 0;
            } else if (type === 'companyNet') {
              let volume = economics?.cashflowNet?.reduce((prev, curr) => prev + curr.boe, 0) / 1000;
              volume -= volume * economics?.settings?.royalty;
              return Number(volume?.toFixed(2)) || 0;
            };
            break;
          
         
          default:
            return 0;
        };
      };


    } catch (error) {
      return [];
    };
  }, [economics]);

  const columns = useMemo(() => {
    return [
      {
        field: 'id',
        headerName: 'Category',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilGross',
        headerName: `Oil Gross (Mbbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilNet',
        headerName: `Oil Net (Mbbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasGross',
        headerName: `Solution Gas Gross (MMscf)`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasNet',
        headerName: 'Solution Gas Net (MMscf)',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasGross',
        headerName: 'Ass. & Non-Ass. Gas Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasNet',
        headerName: 'Ass. & Non-Ass. Gas Net (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'nglGross',
        headerName: 'NGL Gross (Mbbl)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'nglNet',
        headerName: 'NGL Net (Mbbl)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'boeGross',
        headerName: 'BOE Gross (Mboe)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'boeNet',
        headerName: 'BOE Net (Mboe)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      }
    ];
  }, []);

  try {

    if (rows) {
      return (
        <div className={classes.root}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[10,25,50,100]}
            disableSelectionOnClick
            className={classes.table}
            components={{
              Toolbar: GridToolbar,
            }}
            autoHeight={true}
            sx={{fontSize: 12}}
            rowHeight={24}
          />
        </div>
      );
    } else {
      return <></>;
    };

  } catch (error) {
    return <></>
  };

};
